import { EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import * as fromArTemplatesActions from '../actions/annual-report-templates.actions';
import { ANNUAL_REPORT_AR_TEMPLATES_FEATURE_KEY } from '../constants/feature-state-key';
import { IAnnualReportTemplateDTO } from '../interfaces/annual-report-template-dto.interface';
import { IAnnualReportTemplateFeatureState } from '../interfaces/annual-report-template-feature-state.interface';

export const SelectFeature = createFeatureSelector<IAnnualReportTemplateFeatureState>(
    ANNUAL_REPORT_AR_TEMPLATES_FEATURE_KEY,
);

export const adapter: EntityAdapter<IAnnualReportTemplateDTO> = createEntityAdapter<IAnnualReportTemplateDTO>({
    selectId: selectArTemplateId,
});

export const initialState: IAnnualReportTemplateFeatureState = adapter.getInitialState({
    activeArTemplate: null,
    activeBalanceSheetTaxonomy: null,
    activeIncomeStatementTaxonomy: null,
    activeNotesTemplate: null,
    activePdfTemplate: null,
    locales: undefined,
    translations: undefined,
    error: undefined,
});

export function selectArTemplateId(a: IAnnualReportTemplateDTO): string {
    return a.id;
}

const { selectAll } = adapter.getSelectors();

export const SelectAllArTemplates = createSelector(SelectFeature, selectAll);

export const getSelectedArTemplate = (state: IAnnualReportTemplateFeatureState) => state.activeArTemplate;
export const getLocales = (state: IAnnualReportTemplateFeatureState) => state.locales;
export const getTranslations = (state: IAnnualReportTemplateFeatureState) => state.translations;

export const SelectSelectedArTemplate = createSelector(SelectFeature, getSelectedArTemplate);
export const selectLocales = createSelector(SelectFeature, getLocales);
export const selectTranslations = createSelector(SelectFeature, getTranslations);

export const annualReportTemplatesReducer = createReducer(
    initialState,
    on(fromArTemplatesActions.annualReportTemplateActions.createArTemplate, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.createArTemplateSuccess, (state, action) => ({
        ...state,
        activeArTemplate: action.payload,
        error: undefined,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.createArTemplateFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromArTemplatesActions.annualReportTemplateActions.getAllArTemplates, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.getAllArTemplatesSuccess, (state, action) => {
        return adapter.setAll(action.payload, { ...state, error: undefined });
    }),
    on(fromArTemplatesActions.annualReportTemplateActions.getAllArTemplatesFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromArTemplatesActions.annualReportTemplateActions.deleteArTemplate, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.deleteArTemplateSuccess, (state, action) => {
        return adapter.removeOne(action.payload, { ...state });
    }),
    on(fromArTemplatesActions.annualReportTemplateActions.deleteArTemplateFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromArTemplatesActions.annualReportTemplateActions.updateArTemplate, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.updateArTemplateSuccess, (state, action) => ({
        ...state,
        activeArTemplate: action.payload,
        error: undefined,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.updateArTemplateFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromArTemplatesActions.annualReportTemplateActions.getArTemplateById, (state) => ({
        ...state,
        error: undefined,
        activeArTemplate: undefined,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.getArTemplateByIdSuccess, (state, action) => ({
        ...state,
        activeArTemplate: action.payload,
        error: undefined,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.getArTemplateByIdFailed, (state, action) => ({
        ...state,
        activeArTemplate: null,
        error: action.payload,
    })),

    on(fromArTemplatesActions.annualReportTemplateActions.setArBalanceSheetTaxonomy, (state, action) => ({
        ...state,
        activeBalanceSheetTaxonomy: action.payload.balanceTaxonomyId,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.setArIncomeStatementTaxonomy, (state, action) => ({
        ...state,
        activeIncomeStatementTaxonomy: action.payload.incomeTaxonomyId,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.setArNotesTemplate, (state, action) => ({
        ...state,
        activeNotesTemplate: action.payload.notesTemplateId,
    })),
    on(fromArTemplatesActions.annualReportTemplateActions.setPdfInformation, (state, action) => ({
        ...state,
        activePdfTemplate: action.payload.reportingTemplateId,
    })),

    on(fromArTemplatesActions.annualReportTemplateActions.resetSelectedArTemplate, (state) => ({
        ...state,
        activeArTemplate: null,
    })),

    on(fromArTemplatesActions.annualReportTemplateActions.getArTemplateLocales, (state) => ({
        ...state,
        locales: undefined,
    })),

    on(fromArTemplatesActions.annualReportTemplateActions.getArTemplateLocalesSuccess, (state, action) => ({
        ...state,
        locales: action.payload,
    })),

    on(fromArTemplatesActions.annualReportTemplateActions.getArTemplateLocalesFailed, (state, action) => ({
        ...state,
        locales: null,
        error: action.payload,
    })),
    on(fromArTemplatesActions.arTemplateTranslationsActions.onGetTranslationsSuccess, (state, action) => ({
        ...state,
        translations: action.payload,
    })),
    on(fromArTemplatesActions.arTemplateTranslationsActions.onGetTranslationsFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),
);
